(() => {
  if (window.gfAdProductPinLoaded) return;
  window.gfAdProductPinLoaded = true;

  const gridSelector = 'product-list[data-gf-ad-collection]';
  const trackedKeys = new Set(['pin', 'ttclid', 'gclid', 'fbclid', 'preview_theme_id', 'view']);
  const completed = new WeakMap();
  const pageCache = new Map();
  let controller;
  let scheduled = false;

  function pinFrom(url) {
    const value = url.searchParams.get('pin') || '';
    return /^[a-z0-9][a-z0-9-]{0,254}$/.test(value) ? value : '';
  }

  function retainParams(destination, source) {
    if (destination.origin !== source.origin || destination.pathname !== source.pathname) return;
    for (const [key, value] of source.searchParams) {
      if ((trackedKeys.has(key) || key.startsWith('utm_')) && !destination.searchParams.has(key)) {
        destination.searchParams.append(key, value);
      }
    }
  }

  function matchingCard(grid, handle) {
    return Array.from(grid.children).find(card =>
      card.tagName === 'PRODUCT-CARD' && card.getAttribute('handle') === handle
    );
  }

  function prepareNavigation(section, current) {
    section.querySelectorAll('facet-link a[href], a.pagination__item[href]').forEach(link => {
      const destination = new URL(link.href);
      retainParams(destination, current);
      link.href = destination.href;
    });
    section.querySelectorAll('facets-form form').forEach(form => {
      for (const [key, value] of current.searchParams) {
        if (!trackedKeys.has(key) && !key.startsWith('utm_')) continue;
        let input = Array.from(form.elements).find(element => element.name === key);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          form.append(input);
        }
        input.value = value;
      }
    });
  }

  function promote(grid, card, handle) {
    if (card.getAttribute('data-gf-product-available') !== 'true') {
      grid.dataset.gfPinStatus = 'unavailable';
      return;
    }
    card.removeAttribute('reveal-on-scroll');
    card.style.opacity = '1';
    const image = card.querySelector('.product-card__image--primary');
    if (image) {
      image.loading = 'eager';
      image.fetchPriority = 'high';
    }
    if (grid.firstElementChild !== card) grid.prepend(card);
    grid.dataset.gfPinnedHandle = handle;
    grid.dataset.gfPinStatus = 'applied';
  }

  async function pageDocument(url, signal) {
    const key = url.toString();
    if (pageCache.has(key)) return pageCache.get(key);
    const requestController = new AbortController();
    const abort = () => requestController.abort();
    if (signal.aborted) abort();
    signal.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 6000);
    let parsed;
    try {
      const response = await fetch(key, {
        credentials: 'same-origin',
        signal: requestController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Collection section unavailable');
      parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
    }
    if (!parsed.querySelector(gridSelector)) throw new Error('Collection grid unavailable');
    pageCache.set(key, parsed);
    if (pageCache.size > 10) pageCache.delete(pageCache.keys().next().value);
    return parsed;
  }

  async function apply() {
    scheduled = false;
    const current = new URL(window.location.href);
    const handle = pinFrom(current);
    const grid = document.querySelector(gridSelector);
    if (!grid) return;
    const section = grid.closest('.shopify-section');
    const signature = current.pathname + current.search;
    if (completed.get(grid) === signature) return;
    completed.set(grid, signature);
    controller?.abort();
    controller = new AbortController();
    const signal = controller.signal;
    prepareNavigation(section, current);

    if (!handle) {
      grid.dataset.gfPinStatus = current.searchParams.has('pin') ? 'invalid' : 'default';
      return;
    }

    const existing = matchingCard(grid, handle);
    if (Number(grid.dataset.gfAdPage) !== 1) {
      if (existing?.getAttribute('data-gf-product-available') === 'true') existing.remove();
      grid.dataset.gfPinStatus = 'later-page';
      return;
    }

    if (existing) {
      promote(grid, existing, handle);
      return;
    }

    grid.dataset.gfPinStatus = 'searching';
    const pages = Math.min(Number(grid.dataset.gfAdPages) || 1, 10);
    try {
      for (let page = 2; page <= pages; page++) {
        const url = new URL(current);
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', grid.dataset.gfAdSection);
        const parsed = await pageDocument(url, signal);
        if (signal.aborted || !grid.isConnected || window.location.href !== current.href) return;
        const candidate = matchingCard(parsed.querySelector(gridSelector), handle);
        if (!candidate) continue;
        promote(grid, document.importNode(candidate, true), handle);
        return;
      }
      grid.dataset.gfPinStatus = Number(grid.dataset.gfAdPages) > 10 ? 'page-limit' : 'not-found';
    } catch (error) {
      if (!signal.aborted && grid.isConnected) grid.dataset.gfPinStatus = 'fallback';
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(apply);
  }

  document.addEventListener('facet:update', event => {
    const current = new URL(window.location.href);
    const grid = document.querySelector(gridSelector);
    if (!grid || !event.detail?.url) return;
    const destination = event.detail.url;
    retainParams(destination, current);
    if (destination.origin === current.origin && destination.pathname === current.pathname) {
      controller?.abort();
    }
  }, true);

  function start() {
    const main = document.getElementById('main');
    if (!main) return;
    new MutationObserver(records => {
      if (records.some(record => Array.from(record.addedNodes).some(node =>
        node.nodeType === 1 && (node.matches(gridSelector) || node.querySelector(gridSelector))
      ))) schedule();
    }).observe(main, { childList: true, subtree: true });
    window.addEventListener('popstate', schedule);
    document.addEventListener('shopify:section:load', schedule);
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
